import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { router } from './router.tsx';
import { ApiError } from './api/client.ts';
// The playback engine's visual contract. Loaded once here (global, like the app
// CSS). Its class names (.stage/.layer/.media/.overlay-*) are engine-owned and
// don't collide with CMS chrome; app.css scopes `.stage` down from position:fixed
// to fit inside the .engine-frame preview box (see “Playback preview” section).
import '@seencast/player-core/styles.css';
import './styles/index.css';

const queryClient = new QueryClient({
  // A 401 on any query means the session expired (or never existed) — bounce to
  // login. Mutations keep their own 401 handling (e.g. the login form shows the
  // error inline rather than redirecting).
  queryCache: new QueryCache({
    onError: (error) => {
      if (
        error instanceof ApiError &&
        error.isAuth &&
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/accept-invite')
      ) {
        window.location.href = '/login';
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

// The CMS talks to the real API by default (same-origin `/v1`, proxied to the
// Worker in dev — see vite.config.ts). The MSW mock backend is opt-in for
// backend-free demos: set VITE_ENABLE_MOCKS=true.
async function enableMocks() {
  if (import.meta.env.VITE_ENABLE_MOCKS !== 'true') return;
  const { startMockWorker } = await import('./mocks/browser.ts');
  await startMockWorker();
}

async function bootstrap() {
  await enableMocks();
  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root element not found');
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
